import { Editor, Frame } from '@craftjs/core';
import { Box, Typography } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';

import { BlockEditorProvider } from '@/components/blockEditor/BlockEditor.context';
import { CommonData } from '@/components/blockEditor/blockEditor.types';
import { EDUCATION_SLIDE_RESOLVER } from '@/components/blockEditor/presentation/educationSlideResolver';
import { transformCommonDataToCraft } from '@/components/blockEditor/transformCommonDataToCraft';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { PAGE_KIND_TO_EDUCATION_SLIDE_ID } from '@/modules/education/Education.constants';
import {
  isEducationPageOrganizationManaged,
  useCustomSlide,
  useEducationSlideOverride,
  useEducationSlideOverrides,
} from '@/modules/education/EducationSlideOverrides/EducationSlideOverrides.context';
import { getEducationSlideDefaultData } from '@/modules/education/EducationSlideTrowser/EducationSlideDefaultData';
import {
  getSlideIdentifierFromEducationSlideId,
  getSlideIdentifierFromPageKind,
} from '@/modules/education/EducationSlideTrowser/utils/slideIdentifier.utils';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { PresentationBlockHoverRegion } from '@/modules/presentation/components/PresentationHoverActions/PresentationBlockHoverRegion';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { SlideToolbarButton } from '@/modules/slideEditor/SlideToolbar';

import { useIsDesignMode } from '../../../ClientPresentationDesigner.hooks';
import { useRegisterSlide } from '../../../context/ClientPresentationDesigner.paginationContext';
import { ClientPresentationV2Page } from '../../../types/ClientPresentation.PresentationPageType';
import { ClientPresentationFooter } from '../../ClientPresentationFooter';
import { ClientPresentationSlide } from '../../ClientPresentationSlide';
import { BaseBundleSlideProps } from '../BundleSlide.types';
import {
  EducationalPageKind,
  getEducationalPageDefaultDisplayNames,
} from './EducationalSlide.utils';
import { PresentationEditEducationalSlideTrowser } from './PresentationEditEducationalSlideTrowser';

export interface EducationalSlideWrapperProps extends BaseBundleSlideProps {
  children?: React.ReactNode;
  page: ClientPresentationV2Page;
  pageIndex: number;
  hasMorePages?: boolean;
}

export function EducationalSlideWrapper({
  children,
  SlideWrapper = Box,
  page,
  bundle,
  isVisible,
  pageIndex,
  hasMorePages = false,
}: EducationalSlideWrapperProps) {
  const slideId = useRegisterSlide({
    bundleId: bundle.id,
    pageId: page?.id,
    pageIndex,
  });

  const isDesignMode = useIsDesignMode();
  // Only tenant admins (Editor-level branding access) may customize education
  // slides — the same permission that gates the admin education page. Falls
  // back to false when there's no current user (e.g. the read-only/print
  // viewer) so the toolbar never appears there.
  const canEditEducation = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_EDUCATION_ADMIN_PAGE,
    { returnFalseOnMissingUser: true }
  );
  const educationTrowser = useTrowser(TrowserKey.EducationPages);
  const isEditOpen =
    educationTrowser.isOpen && educationTrowser.target === slideId;

  // Get the education slide ID for default data lookup
  const educationSlideId = PAGE_KIND_TO_EDUCATION_SLIDE_ID[page.type];

  // Generate the slide identifier for looking up overrides
  // Uses the utility function to ensure consistency with other components
  const slideIdentifier = getSlideIdentifierFromPageKind(page.type, pageIndex);

  // Wait for overrides to load before mounting the CraftJS Frame, because
  // Frame only reads its `data` prop on initial mount and ignores updates.
  // Without this gate, the Frame can mount with default data before the
  // override query completes, and never pick up the override content.
  const {
    loading: overridesLoading,
    error: overridesError,
    overridesMap,
  } = useEducationSlideOverrides();

  // Check if there's a tenant override for this slide
  const override = useEducationSlideOverride(slideIdentifier);
  const page0Override = educationSlideId
    ? overridesMap[getSlideIdentifierFromEducationSlideId(educationSlideId, 0)]
    : undefined;
  const isOrganizationManaged = educationSlideId
    ? isEducationPageOrganizationManaged(educationSlideId, overridesMap)
    : false;

  // Titles for all pages are stored on page 0's override.
  const slideTitle =
    page0Override?.displayName ??
    getEducationalPageDefaultDisplayNames()[page.type as EducationalPageKind];

  // Load and transform default data for CraftJS
  const defaultData = useMemo(
    () =>
      educationSlideId
        ? getEducationSlideDefaultData(educationSlideId, pageIndex)
        : null,
    [educationSlideId, pageIndex]
  );

  // Use override contentJSON if available, otherwise use default data
  const commonData = useMemo(() => {
    // If there's an override, use it
    if (override?.contentJSON) {
      return override.contentJSON;
    }
    // Otherwise use the default data
    return defaultData;
  }, [override, defaultData]);

  const craftData = useMemo(
    () =>
      commonData && educationSlideId
        ? transformCommonDataToCraft(
            commonData,
            'ROOT',
            'SlideRoot',
            {
              slideId: educationSlideId,
              slideIndex: pageIndex,
            },
            true // useLinkedNodesForRootChildren
          )
        : null,
    [commonData, educationSlideId, pageIndex]
  );

  // The CraftJS `Frame` only reads its `data` prop on mount and ignores later
  // updates. Keying the `Editor` on the content forces a remount when an edit
  // changes the override content (after the trowser's save refetches
  // `GetEducationPageEditor`), so the slide reflects the new content in place.
  // Unchanged content serializes to the same key, so there's no spurious remount.
  const contentKey = useMemo(() => JSON.stringify(commonData), [commonData]);

  // Show the hover "Edit page" toolbar only in the designer, and only to users
  // who can customize education slides. Requires a resolvable education slide
  // id since the editor keys off it.
  const showEditToolbar =
    isDesignMode &&
    canEditEducation &&
    Boolean(educationSlideId) &&
    !isOrganizationManaged;

  if (!isVisible) {
    return null;
  }

  // Signal to the print watcher that this education slide is still loading
  // override data. The watcher polls this attribute and won't capture the PDF
  // until all education slides report ready. This follows the same pattern as
  // waterfalls, entities, family trees, etc.
  if (overridesLoading || overridesError) {
    return <SlideWrapper data-presentationeducationready="false" />;
  }

  const slideContent = (
    <Editor
      key={contentKey}
      resolver={EDUCATION_SLIDE_RESOLVER}
      enabled={false}
    >
      <BlockEditorProvider isEditMode={false} rootNodeId="ROOT">
        {craftData ? <Frame data={craftData} /> : children}
      </BlockEditorProvider>
    </Editor>
  );

  return (
    <SlideWrapper data-presentationeducationready="true">
      <ClientPresentationSlide
        id={slideId}
        footer={<ClientPresentationFooter slideId={slideId} />}
        leftHeaderContent={
          <PresentationSlide.MainHeading
            heading={pageIndex > 0 ? `${slideTitle} (continued)` : slideTitle}
          />
        }
      >
        <EducationSlideContentWrapper hasMorePages={hasMorePages}>
          {/* Hover region sits inside the content wrapper's padding and hugs
              the rendered content, so the outline tracks the slide body rather
              than the full padded slide area. The 8px padding gives the outline
              breathing room from the content; the matching negative margin
              absorbs it into the wrapper's padding so the content doesn't shift.
              It also mirrors the wrapper's full-height flex column so the slide
              content (SlideRoot uses flex: 1) still stretches to fill the body;
              `calc(100% + 16px)` offsets the 8px top/bottom negative margins so
              the inner content area resolves to the original height. */}
          {showEditToolbar ? (
            <PresentationBlockHoverRegion
              id={`${slideId}-educational`}
              disabled={isEditOpen}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% + 16px)',
                p: 1,
                m: -1,
              }}
              renderToolbar={({ dismiss }) => (
                <SlideToolbarButton
                  variant="transparent"
                  startIcon={Edit02Icon}
                  onClick={() => {
                    dismiss();
                    educationTrowser.open({ target: slideId });
                  }}
                >
                  Edit page
                </SlideToolbarButton>
              )}
            >
              {slideContent}
            </PresentationBlockHoverRegion>
          ) : (
            slideContent
          )}
        </EducationSlideContentWrapper>
      </ClientPresentationSlide>
      {isEditOpen && showEditToolbar && educationSlideId && (
        <PresentationEditEducationalSlideTrowser
          educationSlideId={educationSlideId}
          pageIndex={pageIndex}
          onClose={educationTrowser.close}
        />
      )}
    </SlideWrapper>
  );
}

export interface EducationSlideContentWrapperProps extends PropsWithChildren {
  hasMorePages?: boolean;
}

export function EducationSlideContentWrapper({
  children,
  hasMorePages = false,
}: EducationSlideContentWrapperProps) {
  return (
    <Box
      sx={{
        p: 4,
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}

      {hasMorePages && (
        <Box
          sx={(t) => ({
            position: 'absolute',
            right: t.spacing(3),
            bottom: 0,
          })}
        >
          <Typography variant="body-xs">Continued on next page</Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * Wrapper for custom education slides.
 * Similar to EducationalSlideWrapper but loads content from custom slides instead of template overrides.
 */
export interface CustomEducationSlideWrapperProps extends BaseBundleSlideProps {
  page: ClientPresentationV2Page;
  customSlideIdentifier: string;
}

export function CustomEducationSlideWrapper({
  SlideWrapper = Box,
  page,
  bundle,
  isVisible,
  customSlideIdentifier,
}: CustomEducationSlideWrapperProps) {
  const customSlide = useCustomSlide(customSlideIdentifier);

  const slideId = useRegisterSlide({
    bundleId: bundle.id,
    pageId: page?.id,
    pageIndex: 0,
  });

  const commonData = customSlide?.contentJSON as CommonData | undefined;

  const craftData = useMemo(() => {
    if (!commonData?.blocks || !Array.isArray(commonData.blocks)) {
      return null;
    }

    return transformCommonDataToCraft(
      commonData,
      'ROOT',
      'SlideRoot',
      {
        slideId: customSlideIdentifier,
        slideIndex: 0,
      },
      true // useLinkedNodesForRootChildren
    );
  }, [commonData, customSlideIdentifier]);

  if (!isVisible || !customSlide || !craftData) {
    return null;
  }

  const slideTitle = customSlide.displayName || 'Custom page';

  return (
    <SlideWrapper>
      <ClientPresentationSlide
        id={slideId}
        footer={<ClientPresentationFooter slideId={slideId} />}
        leftHeaderContent={
          <PresentationSlide.MainHeading heading={slideTitle} />
        }
      >
        <EducationSlideContentWrapper hasMorePages={false}>
          <Editor resolver={EDUCATION_SLIDE_RESOLVER} enabled={false}>
            <BlockEditorProvider isEditMode={false} rootNodeId="ROOT">
              <Frame data={craftData} />
            </BlockEditorProvider>
          </Editor>
        </EducationSlideContentWrapper>
      </ClientPresentationSlide>
    </SlideWrapper>
  );
}
